"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, ArrowRight, ArrowLeft, Star, Sparkles, CheckCircle, Calendar } from "lucide-react";
import { treatments } from "@/lib/treatments-data";
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useMediaQuery } from "@/hooks/use-media-query";
import Link from "next/link";
import SectionTitle from "@/components/home/section-title";
import BookingForm from "@/components/treatments/booking-form";
import { Icon } from "@iconify/react";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { useParams } from "next/navigation";
import { Locale, isRTLLanguage } from "@/lib/i18n/config";

gsap.registerPlugin(ScrollTrigger);

const STACK_CARD_OFFSET_Y = 12;
const MOBILE_CARD_VISIBLE_HEIGHT_VH = 75;

// Floating sparkles component for mobile
const FloatingSparkles = ({ className = "" }: { className?: string }) => {
  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-gold-500 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 1, 0],
            rotate: [0, 360],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// CTA Content component with booking form toggle
const CTAContent = () => {
  const { t } = useTranslation('home');
  const { t: tForms } = useTranslation('forms');
  const { toast } = useToast();
  const params = useParams();
  const locale = params.locale as string;
  const isRTL = isRTLLanguage(locale);
  const [showBooking, setShowBooking] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    age: '',
    consultationTopic: '',
    date: null as Date | null,
    timeSlot: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    try {
      const draft = localStorage.getItem('bookingFormDraft');
      if (draft) {
        const parsed = JSON.parse(draft);
        setFormData(prev => ({ ...prev, ...parsed, date: parsed.date ? new Date(parsed.date) : null }));
      }
    } catch { /* ignore corrupt localStorage */ }
  }, []);
  const [isTimeSlotOpen, setIsTimeSlotOpen] = useState(false);

  const countries = [
    // Top priority countries
    'Turkey', 'United Kingdom', 'United States', 'Germany', 'Netherlands',
    'France', 'Italy', 'Spain', 'Canada', 'Saudi Arabia', 'Russia',
    'Norway', 'Denmark', 'Sweden', 'Australia', 'Austria', 'Belgium',
    'Switzerland', 'UAE', 'Qatar', 'Kuwait',
    // All other countries alphabetically
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia',
    'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Bolivia', 'Bosnia and Herzegovina',
    'Brazil', 'Brunei', 'Bulgaria', 'Cambodia', 'Chile', 'China', 'Colombia', 'Croatia',
    'Cyprus', 'Czech Republic', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Fiji',
    'Finland', 'Georgia', 'Ghana', 'Greece', 'Hungary', 'Iceland', 'India',
    'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Ivory Coast', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Libya', 'Lithuania',
    'Luxembourg', 'Malaysia', 'Malta', 'Mexico', 'Moldova', 'Mongolia', 'Morocco', 'Myanmar', 'Nepal',
    'New Zealand', 'Nigeria', 'Oman', 'Pakistan', 'Papua New Guinea', 'Paraguay',
    'Peru', 'Philippines', 'Poland', 'Portugal', 'Romania', 'Rwanda',
    'Senegal', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Sri Lanka',
    'Sudan', 'Syria', 'Tajikistan', 'Tanzania', 'Thailand', 'Tunisia',
    'Turkmenistan', 'Ukraine', 'Uruguay', 'Uzbekistan',
    'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  const consultationTopics = [
    { value: 'general', label: tForms('booking.consultationTopics.general') },
    { value: 'cosmetic', label: tForms('booking.consultationTopics.cosmetic') },
    { value: 'dental-veneers', label: tForms('booking.consultationTopics.veneers') },
    { value: 'teeth-whitening', label: tForms('booking.consultationTopics.whitening') },
    { value: 'dental-implants', label: tForms('booking.consultationTopics.implants') },
    { value: 'orthodontics', label: tForms('booking.consultationTopics.orthodontics') },
    { value: 'hollywood-smile', label: tForms('booking.consultationTopics.hollywoodSmile') },
    { value: 'root-canal', label: tForms('booking.consultationTopics.rootCanal') },
    { value: 'oral-surgery', label: tForms('booking.consultationTopics.oralSurgery') },
    { value: 'preventive', label: tForms('booking.consultationTopics.preventiveCare') },
    { value: 'other', label: tForms('booking.consultationTopics.other') }
  ];

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
    '05:00 PM', '05:30 PM', '06:00 PM', '06:30 PM',
    '07:00 PM', '07:30 PM', '08:00 PM'
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try { localStorage.setItem('bookingFormDraft', JSON.stringify(formData)); } catch { /* ignore */ }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (!res.ok) {
        throw new Error(`Booking failed: ${res.status}`);
      }

      setFormData({
        name: '', email: '', phone: '', country: '', age: '',
        consultationTopic: '', date: null, timeSlot: '', message: ''
      });
      try { localStorage.removeItem('bookingFormDraft'); } catch { /* ignore */ }

      toast({ title: tForms('booking.successMessage') });
      setShowBooking(false);
    } catch (err) {
      clearTimeout(timeout);
      if (process.env.NODE_ENV !== 'production') {
        console.error('[handleSubmit] Booking error:', err);
      }
      const message = err instanceof Error && err.name === 'AbortError'
        ? tForms('booking.timeoutMessage') ?? 'Request timed out. Please try again.'
        : tForms('booking.errorMessage') ?? 'Something went wrong. Please try again.';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showBooking) {
    return (
      <CardContent className="relative p-6 md:p-8 w-full h-[75vh] md:h-[650px] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className={`text-xl md:text-2xl font-bold text-white font-serif ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('featuredTreatments.consultation.title')} <span className="text-gold-500">{t('featuredTreatments.consultation.free')}</span>
            </h3>
            <button
              onClick={() => setShowBooking(false)}
              className={`text-slate-400 hover:text-white transition-colors p-2 ${isRTL ? 'ml-auto' : 'mr-auto'}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name and Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className={isRTL ? 'md:order-2' : ''}>
                  <input
                    type="text"
                    placeholder={tForms('booking.fields.fullName')}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                  />
                </div>
                <div className={isRTL ? 'md:order-1' : ''}>
                  <input
                    type="email"
                    placeholder={tForms('booking.fields.email')}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                  />
                </div>
              </div>

              {/* Phone and Age */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className={isRTL ? 'md:order-2' : ''}>
                  <input
                    type="tel"
                    placeholder={tForms('booking.fields.phone')}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                  />
                </div>
                <div className={isRTL ? 'md:order-1' : ''}>
                  <input
                    type="text"
                    placeholder={tForms('booking.fields.age')}
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                  />
                </div>
              </div>

              {/* Country and Consultation Topic */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className={isRTL ? 'md:order-2' : ''}>
                  <select
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <option value="">{tForms('booking.placeholders.selectCountry')}</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div className={isRTL ? 'md:order-1' : ''}>
                  <select
                    value={formData.consultationTopic}
                    onChange={(e) => handleInputChange('consultationTopic', e.target.value)}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <option value="">{tForms('booking.placeholders.selectConsultationTopic')}</option>
                    {consultationTopics.map(topic => (
                      <option key={topic.value} value={topic.value}>{topic.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className={isRTL ? 'md:order-2' : ''}>
                  <label className={`block text-sm font-medium text-slate-200 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {tForms('booking.fields.preferredDate')} *
                  </label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.date ? formData.date.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleInputChange('date', new Date(e.target.value));
                      }
                    }}
                    required
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    style={{
                      colorScheme: 'dark'
                    }}
                  />
                </div>

                <div className={`relative ${isRTL ? 'md:order-1' : ''}`}>
                  <label className={`block text-sm font-medium text-slate-200 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {tForms('booking.fields.preferredTime')} *
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsTimeSlotOpen(!isTimeSlotOpen)}
                    className={`w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 flex items-center justify-between ${isRTL ? 'text-right flex-row-reverse' : 'text-left'}`}
                  >
                    <span className={formData.timeSlot ? 'text-white' : 'text-slate-400'}>
                      {formData.timeSlot || tForms('booking.placeholders.selectPreferredTime')}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gold-500 transition-transform duration-200 ${isTimeSlotOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isTimeSlotOpen && (
                    <>
                      {/* Overlay to close dropdown when clicking outside */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsTimeSlotOpen(false)}
                      />

                      <div
                        className="absolute top-full left-0 mt-2 z-50 bg-navy-800 border border-gold-500/30 rounded-lg p-3 w-full max-h-48 overflow-y-auto shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="grid grid-cols-2 gap-2">
                          {timeSlots.map(time => (
                            <button
                              key={time}
                              type="button"
                              onClick={() => {
                                handleInputChange('timeSlot', time);
                                setIsTimeSlotOpen(false);
                              }}
                              className={`px-3 py-2 text-sm rounded transition-all duration-200 ${formData.timeSlot === time
                                ? 'bg-gold-500 text-navy-900 font-semibold'
                                : 'bg-navy-700/50 text-slate-300 hover:bg-gold-500/20 hover:text-gold-500'
                                }`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <textarea
                  placeholder={tForms('booking.placeholders.additionalMessage')}
                  rows={3}
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="w-full px-4 py-3 bg-navy-800/50 border border-gold-500/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                />
              </div>

              {/* Submit Button */}
              <div className={`flex gap-4 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 px-6 py-3 bg-gold-500 hover:bg-gold-500/90 disabled:opacity-50 text-navy-900 font-bold text-base rounded-xl shadow-xl shadow-gold-500/20 hover:shadow-gold-500/30 transition-all duration-300 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`animate-spin rounded-full h-5 w-5 border-b-2 border-navy-900 ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                      {tForms('booking.buttons.booking')}
                    </div>
                  ) : (
                    tForms('booking.buttons.bookConsultation')
                  )}
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => setShowBooking(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 bg-transparent border border-gold-500/30 hover:border-gold-500/50 text-gold-500 font-semibold rounded-xl transition-all duration-300"
                >
                  {tForms('buttons.cancel') ?? 'Cancel'}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </CardContent>
    );
  }

  return (
    <CardContent className="relative p-6 md:p-8 text-center w-full h-[75vh] md:h-[650px] flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.9 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="space-y-4 max-w-2xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 bg-gold-500/20 rounded-full mb-3">
          <Sparkles className="w-6 h-6 text-gold-500" />
        </div>

        <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 font-serif leading-tight">
          {t('featuredTreatments.consultation.subtitle')}{' '}
          <span className="text-gold-500">{t('featuredTreatments.consultation.rightForYou')}</span>
          {t('featuredTreatments.consultation.subtitle2') && ` ${t('featuredTreatments.consultation.subtitle2')}`}
        </h3>

        <p className="text-slate-200 text-base md:text-lg mb-6 leading-relaxed">
          {t('featuredTreatments.consultation.description')}
        </p>

        <motion.button
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowBooking(true)}
          className={`inline-flex items-center px-6 py-3 bg-gold-500 hover:bg-gold-500/90 text-navy-900 font-bold text-base rounded-2xl shadow-xl shadow-gold-500/20 hover:shadow-gold-500/30 transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          {isRTL ? (
            <ArrowLeft className="w-5 h-5 ml-3" />
          ) : (
            <ArrowRight className="w-5 h-5 mr-3" />
          )}
          {t('featuredTreatments.consultation.cta')}
        </motion.button>

        <div className={`flex items-center justify-center gap-4 mt-6 text-slate-300 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-gold-500" />
            <span>{t('featuredTreatments.consultation.benefits.free')}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-gold-500" />
            <span>{t('featuredTreatments.consultation.benefits.expert')}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-gold-500" />
            <span>{t('featuredTreatments.consultation.benefits.noCommitment')}</span>
          </div>
        </div>
      </motion.div>
    </CardContent>
  );
};

// Background pattern component
const BackgroundPattern = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Light mode - clean and elegant */}
      <div className="absolute inset-0 dark:hidden">
        {/* Base clean gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50/80 to-slate-100/60" />

        {/* Subtle accent glow */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.12) 0%, rgba(212, 175, 55, 0.04) 40%, transparent 70%)'
          }}
        />

        {/* Secondary glow for depth */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 80% 80%, rgba(14, 26, 50, 0.08) 0%, transparent 50%)'
          }}
        />
      </div>

      {/* Dark mode - sophisticated and clean */}
      <div className="hidden dark:block absolute inset-0">
        {/* Base dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900" />

        {/* Gold accent */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 35%, transparent 65%)'
          }}
        />

        {/* Blue accent for contrast */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)'
          }}
        />
      </div>

      {/* Clean dot pattern */}
      <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.08]">
        <svg className="w-full h-full" viewBox="0 0 50 50" preserveAspectRatio="none">
          <defs>
            <pattern id="clean-dots" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
              <circle cx="25" cy="25" r="1" fill="currentColor" className="text-gold-500" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#clean-dots)" />
        </svg>
      </div>

      {/* Subtle animated elements for dark mode only */}
      <div className="hidden dark:block relative">
        {[...Array(6)].map((_, i) => {
          // Use deterministic positions based on index to avoid hydration mismatch
          const positions = [
            { left: '30%', top: '45%' },
            { left: '21%', top: '26%' },
            { left: '29%', top: '78%' },
            { left: '45%', top: '59%' },
            { left: '78%', top: '69%' },
            { left: '68%', top: '25%' }
          ];
          return (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gold-500/30 rounded-full animate-pulse"
            style={{
              ...positions[i],
              animationDuration: `${4 + (i * 0.3)}s`,
              animationDelay: `${i * 0.5}s`
            }}
          />
          );
        })}
      </div>

      {/* Light mode minimal particles */}
      <div className="block dark:hidden relative">
        {[...Array(4)].map((_, i) => {
          // Use deterministic positions for light mode particles
          const lightPositions = [
            { left: '60%', top: '27%' },
            { left: '44%', top: '42%' },
            { left: '50%', top: '69%' },
            { left: '49%', top: '33%' }
          ];
          return (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 bg-gold-500/20 rounded-full animate-pulse"
            style={{
              ...lightPositions[i],
              animationDuration: `${6 + (i * 0.2)}s`,
              animationDelay: `${i * 0.3}s`
            }}
          />
          );
        })}
      </div>
    </div>
  );
};

export default function FeaturedTreatments() {
  const { t, locale } = useTranslation('home');
  const params = useParams();
  const currentLocale = (params?.locale as Locale) || locale;
  const isRTL = isRTLLanguage(currentLocale);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const mobileAnimationContainerRef = useRef<HTMLDivElement>(null);
  const mobileCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const desktopCardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isMobile = useMediaQuery("(max-width: 767px)");
  const [isClient, setIsClient] = useState(false);

  const { scrollYProgress: sectionScrollYProgress } = useScroll({
    target: mainContainerRef,
    offset: ["start end", "start 0.6"]
  });

  const sectionY = useTransform(sectionScrollYProgress, [0, 1], [100, 0]);
  const sectionOpacity = useTransform(sectionScrollYProgress, [0, 1], [0, 1]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const numTreatments = treatments.length;
  // Disable y transform on mobile — it conflicts with GSAP pinning and causes layout jump
  const sectionStyle = isClient
    ? isMobile
      ? { y: 0, opacity: sectionOpacity }
      : { y: sectionY, opacity: sectionOpacity }
    : { y: 0, opacity: 1 };

  useEffect(() => {
    if (!isClient) return;

    let mobileTl: gsap.core.Timeline | null = null;
    const stInstances: ScrollTrigger[] = [];

    if (isMobile) {
      if (
        !mobileAnimationContainerRef.current ||
        mobileCardRefs.current.length === 0
      )
        return;

      const validMobileCards = mobileCardRefs.current.filter(
        (el) => el !== null
      ) as HTMLDivElement[];
      if (validMobileCards.length === 0) return;

      // Mobile setup - all cards in stack have full opacity
      validMobileCards.forEach((card, index) => {
        gsap.set(card, {
          y: index * STACK_CARD_OFFSET_Y,
          height: `${MOBILE_CARD_VISIBLE_HEIGHT_VH}vh`,
          position: "absolute",
          width: "95%", // Reduced from 100% to prevent cutoff
          left: "2.5%", // Center the card
          zIndex: numTreatments - index,
          opacity: 1, // All cards start with full opacity
          xPercent: 0,
          scale: 1 - (index * 0.01), // Very subtle scale difference
          filter: "blur(0px)", // No blur at all
        });
      });

      mobileTl = gsap.timeline({
        scrollTrigger: {
          trigger: mobileAnimationContainerRef.current,
          pin: true,
          scrub: 4,
          start: "top 8%",
          end: `+=${(numTreatments - 1) * 200 + 246}vh`,
          pinSpacing: false,
          onUpdate: (self) => {
            // Keep all non-sliding cards at full opacity
            const progress = self.progress;
            validMobileCards.forEach((card, index) => {
              if (card) {
                const cardProgress = Math.max(0, (progress * (numTreatments - 1)) - index);
                if (cardProgress < 0.1) {
                  // Card hasn't started sliding yet - keep at full opacity
                  gsap.set(card, { opacity: 1 });
                }
              }
            });
          }
        },
      });
      stInstances.push(mobileTl.scrollTrigger as ScrollTrigger);

      validMobileCards.slice(0, -1).forEach((card, index) => {
        if (mobileTl) {
          mobileTl.to(
            card,
            {
              xPercent: 100,
              rotationY: -15,
              scale: 0.8,
              opacity: 0,
              filter: "blur(0px)",
              ease: "expo.out",
              duration: 10,
            },
            index * 2.4
          );
        }
      });

      return () => {
        if (mobileTl) mobileTl.kill();
        stInstances.forEach((st) => st.kill());
        validMobileCards.forEach((card) =>
          gsap.set(card, { clearProps: "all" })
        );
      };
    }
  }, [isMobile, isClient, numTreatments]);

  const mobileContainerEffectiveHeight =
    isClient && isMobile
      ? `${MOBILE_CARD_VISIBLE_HEIGHT_VH +
      (numTreatments - 1) * STACK_CARD_OFFSET_Y
      }vh`
      : "auto";

  return (
    <section className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900 overflow-hidden">
      <BackgroundPattern />

      <motion.div
        ref={mainContainerRef}
        style={isClient ? sectionStyle : { opacity: 0 }}
        className="relative z-10"
      >
        {/* Container for proper spacing */}
        <div className="container relative">
          {/* Section Title using the shared component */}
          <SectionTitle>{t('featuredTreatments.title')}</SectionTitle>

          {/* Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16 px-4"
          >
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              {t('featuredTreatments.subtitle')}
            </p>
          </motion.div>

          {/* Mobile scroll hint */}
          {isClient && isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-8 px-4"
            >
              <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4 text-gold-500" />
                {t('featuredTreatments.scrollPrompt')}
                <motion.span
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  ↓
                </motion.span>
              </p>
            </motion.div>
          )}

          {/* Desktop Grid View - Changed to 3 columns for 2 rows */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 px-8 max-w-8xl mx-auto">
            {treatments.map((treatment, index) => (
              <motion.div
                key={treatment.id}
                ref={(el) => {
                  if (!isMobile) desktopCardRefs.current[index] = el;
                }}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="h-full group"
              >
                <Link href={`/treatments/${treatment.slug}`}>
                  <Card className="relative h-full bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700 text-white backdrop-blur-lg border border-gold-500/20 hover:border-gold-500/40 rounded-2xl overflow-hidden shadow-2xl shadow-navy-900/20 hover:shadow-2xl hover:shadow-gold-500/20 transition-all duration-500 group-hover:scale-[1.02] cursor-pointer">

                    {/* Hover shimmer overlay */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
                      style={{
                        backgroundImage: `
                          linear-gradient(135deg, #222D52 0%, #2e3b63 50%, #3a4974 100%),
                          radial-gradient(circle at top left, rgba(212,175,55, 0.08), transparent 70%)
                        `
                      }}
                    />

                    {/* Image container - Reduced height for shorter cards */}
                    <div className="relative h-48 overflow-hidden">
                      <motion.img
                        src={treatment.heroImage}
                        alt={treatment.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      />

                      {/* Success rate badge */}
                      <div className="absolute top-4 right-4 bg-gold-500/90 backdrop-blur-sm text-navy-900 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {treatment.successRate}
                      </div>
                    </div>

                    {/* Content section - Adjusted height for shorter cards */}
                    <CardContent className="p-6 flex flex-col justify-between h-50 relative">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold-500/90 transition-colors duration-300">
                          {t(`featuredTreatments.treatments.${treatment.id}.title`)}
                        </h3>
                        <p className="text-slate-200 text-sm mb-4 leading-relaxed">
                          {t(`featuredTreatments.treatments.${treatment.id}.description`)}
                        </p>
                        <div className="flex items-center text-slate-300 text-sm mb-4">
                          <Clock className="h-4 w-4 mr-2 text-gold-500" />
                          <span>{t('featuredTreatments.treatmentTime')} {t(`featuredTreatments.treatments.${treatment.id}.treatmentTime`)}</span>
                        </div>
                      </div>

                      {/* CTA Button */}
                      <motion.div
                        className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}
                        whileHover={{ x: isRTL ? -5 : 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <span className={`inline-flex items-center text-gold-500 font-semibold text-sm group-hover:text-gold-500/80 transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {t('featuredTreatments.learnMore')}
                          {isRTL ? (
                            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                          ) : (
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                          )}
                        </span>
                      </motion.div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Mobile Deck of Cards View */}
          {isClient && isMobile && (
            <div
              ref={mobileAnimationContainerRef}
              className="relative mt-12 mx-auto px-2 overflow-hidden"
              style={{ minHeight: mobileContainerEffectiveHeight, width: "100%" }}
            >
              <FloatingSparkles className="z-0" />

              {treatments.map((treatment, index) => (
                <div
                  key={treatment.id}
                  ref={(el) => {
                    mobileCardRefs.current[index] = el;
                  }}
                  className="w-full relative group"
                  style={{ perspective: "1000px" }}
                >
                  <Link href={`/treatments/${treatment.slug}`}>
                    <Card className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700 text-white backdrop-blur-xl border border-gold-500/30 rounded-2xl overflow-hidden shadow-2xl shadow-navy-900/20 h-full flex flex-col">

                      {/* Mobile sparkles overlay */}
                      <div className="absolute inset-0 pointer-events-none z-10">
                        <FloatingSparkles />
                      </div>

                      {/* Mobile hover shimmer overlay */}
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl z-5"
                        style={{
                          backgroundImage: `
                            linear-gradient(135deg, #222D52 0%, #2e3b63 50%, #3a4974 100%),
                            radial-gradient(circle at top left, rgba(212,175,55, 0.08), transparent 70%)
                          `
                        }}
                      />

                      {/* Mobile image container - No background overlay */}
                      <div className="relative h-[55%] w-full overflow-hidden">
                        <img
                          src={treatment.heroImage}
                          alt={treatment.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />

                        {/* Success rate badge */}
                        <div className="absolute top-4 right-4 bg-gold-500/95 backdrop-blur-sm text-navy-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {treatment.successRate}
                        </div>
                      </div>

                      <CardContent className="p-5 flex-1 flex flex-col justify-between relative z-10 bg-gradient-to-br from-navy-900/90 via-navy-800/90 to-navy-700/90">
                        <div>
                          <h3 className="text-lg font-bold text-white mb-2">
                            {t(`featuredTreatments.treatments.${treatment.id}.title`)}
                          </h3>
                          <p className="text-slate-200 text-sm mb-3 leading-relaxed line-clamp-2">
                            {t(`featuredTreatments.treatments.${treatment.id}.description`)}
                          </p>
                          <div className="flex items-center text-slate-300 text-sm mb-3">
                            <Clock className="h-3 w-3 mr-1.5 text-gold-500" />
                            <span>{t('featuredTreatments.treatmentTime')} {t(`featuredTreatments.treatments.${treatment.id}.treatmentTime`)}</span>
                          </div>
                        </div>

                        {/* CTA */}
                        <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                          <span className={`inline-flex items-center text-gold-500 font-semibold text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                            {t('featuredTreatments.learnMore')}
                            {isRTL ? (
                              <ArrowLeft className="w-3 h-3 mr-1.5" />
                            ) : (
                              <ArrowRight className="w-3 h-3 ml-1.5" />
                            )}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* Consultation CTA Section - Adjusted spacing and sizing */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6, margin: "-300px 0px 0px 0px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 px-4 max-w-4xl mx-auto"
          >
            <Card className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700 border border-gold-500/30 rounded-3xl overflow-hidden shadow-2xl shadow-navy-900/20 min-h-[75vh] md:min-h-[650px]">
              {/* Background decorations with custom colors */}
              <div className="absolute inset-0">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold-500 opacity-5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gold-500 opacity-5 rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-gold-500/3 to-gold-500/3 rounded-full blur-3xl"></div>
              </div>

              {/* Dental and Question Mark Background Elements - CTA Section Only */}
              <div className="absolute inset-0 pointer-events-none opacity-[0.12] overflow-hidden">
                {/* Animated Question Marks - Properly contained within CTA */}
                <div
                  className="absolute top-[8%] left-[10%] text-4xl text-gold-500 font-bold transform rotate-12 animate-bounce"
                  style={{
                    animationDuration: '4s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  ?
                </div>

                <div
                  className="absolute bottom-[8%] right-[10%] text-3xl text-gold-500 font-bold transform -rotate-12 animate-pulse"
                  style={{
                    animationDuration: '5s',
                    animationDelay: '1s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  ?
                </div>

                <div
                  className="absolute top-[45%] right-[8%] text-4xl text-gold-500 font-bold transform rotate-45 animate-bounce"
                  style={{
                    animationDuration: '6s',
                    animationDelay: '2s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  ?
                </div>

                <div
                  className="absolute bottom-[20%] left-[80%] text-2xl text-gold-500 font-bold transform -rotate-45 animate-pulse"
                  style={{
                    animationDuration: '3.5s',
                    animationDelay: '0.5s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  ?
                </div>

                <div
                  className="absolute top-[25%] right-[25%] text-2xl text-gold-500 font-bold transform rotate-30 animate-bounce"
                  style={{
                    animationDuration: '4.5s',
                    animationDelay: '2.5s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  ?
                </div>

                <div
                  className="absolute bottom-[10%] left-[15%] text-3xl text-gold-500 font-bold transform -rotate-20 animate-bounce"
                  style={{
                    animationDuration: '5.5s',
                    animationDelay: '1.5s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  ?
                </div>

                {/* Dental Icons - Properly contained within CTA */}
                <div
                  className="absolute top-[15%] right-[30%] animate-bounce"
                  style={{
                    animationDuration: '4.5s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  <Icon
                    icon="mdi:tooth"
                    className="w-12 h-12 text-gold-500"
                  />
                </div>

                <div
                  className="absolute bottom-[25%] left-[8%] animate-pulse"
                  style={{
                    animationDuration: '5.5s',
                    animationDelay: '1s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  <Icon
                    icon="material-symbols:chair"
                    className="w-10 h-10 text-gold-500"
                  />
                </div>

                <div
                  className="absolute top-[70%] right-[12%] animate-pulse"
                  style={{
                    animationDuration: '3s',
                    animationDelay: '2s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  <Icon
                    icon="mdi:tooth-outline"
                    className="w-8 h-8 text-gold-500"
                  />
                </div>

                <div
                  className="absolute top-[12%] left-[5%] animate-bounce"
                  style={{
                    animationDuration: '4s',
                    animationDelay: '0.5s',
                    animationIterationCount: 'infinite'
                  }}
                >
                  <Icon
                    icon="vaadin:dental-chair"
                    className="w-8 h-8 text-gold-500"
                  />
                </div>

                <motion.div
                  className="absolute bottom-[40%] right-[20%]"
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 0.8, 1]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  <Icon
                    icon="material-symbols:dentistry"
                    className="w-6 h-6 text-gold-500"
                  />
                </motion.div>

                <motion.div
                  className="absolute top-[55%] left-[5%]"
                  animate={{
                    y: [0, -5, 0],
                    rotate: [0, -12, 0]
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5
                  }}
                >
                  <Icon
                    icon="tabler:dental-broken"
                    className="w-10 h-10 text-gold-500"
                  />
                </motion.div>

                <motion.div
                  className="absolute bottom-[12%] right-[40%]"
                  animate={{
                    x: [0, -4, 0],
                    y: [0, 6, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2.5
                  }}
                >
                  <Icon
                    icon="healthicons:health-worker"
                    className="w-8 h-8 text-gold-500"
                  />
                </motion.div>

                <motion.div
                  className="absolute top-[35%] left-[12%]"
                  animate={{
                    rotate: [0, -180, -360],
                    scale: [1, 1.2, 1]
                  }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Icon
                    icon="mdi:medical-cross"
                    className="w-6 h-6 text-gold-500"
                  />
                </motion.div>

                <motion.div
                  className="absolute bottom-[15%] left-[70%]"
                  animate={{
                    y: [0, -8, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 3
                  }}
                >
                  <Icon
                    icon="mdi:tooth"
                    className="w-8 h-8 text-gold-500"
                  />
                </motion.div>

                <motion.div
                  className="absolute top-[40%] right-[15%]"
                  animate={{
                    rotate: [0, 15, 0],
                    x: [0, 4, 0]
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.8
                  }}
                >
                  <Icon
                    icon="material-symbols:dentistry"
                    className="w-10 h-10 text-gold-500"
                  />
                </motion.div>

                {/* Dental Images - Bridge at top left as requested */}
                <motion.div
                  className="absolute top-[8%] left-[8%]"
                  animate={{
                    scale: [1, 0.9, 1],
                    rotate: [0, 8, 0],
                    y: [0, 6, 0]
                  }}
                  transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 3
                  }}
                >
                  <img
                    src="/images/icons/bridge.webp"
                    alt="Dental Bridge"
                    className="w-14 h-14 opacity-90"
                  />
                </motion.div>

                <motion.div
                  className="absolute top-[18%] right-[50%]"
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 6, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                >
                  <img
                    src="/images/icons/dental-implant.webp"
                    alt="Dental Implant"
                    className="w-12 h-12 opacity-90"
                  />
                </motion.div>

                <motion.div
                  className="absolute bottom-[30%] left-[30%]"
                  animate={{
                    x: [0, 8, 0],
                    y: [0, -5, 0],
                    rotate: [0, -8, 0]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }}
                >
                  <img
                    src="/images/icons/braces.webp"
                    alt="Dental Braces"
                    className="w-12 h-12 opacity-90"
                  />
                </motion.div>

                <motion.div
                  className="absolute bottom-[50%] right-[70%]"
                  animate={{
                    y: [0, -6, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2.5
                  }}
                >
                  <img
                    src="/images/icons/dental-implant.webp"
                    alt="Dental Implant"
                    className="w-8 h-8 opacity-80"
                  />
                </motion.div>

                <motion.div
                  className="absolute top-[65%] right-[25%]"
                  animate={{
                    x: [0, -5, 0],
                    rotate: [0, 6, 0]
                  }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 4
                  }}
                >
                  <img
                    src="/images/icons/braces.webp"
                    alt="Dental Braces"
                    className="w-10 h-10 opacity-80"
                  />
                </motion.div>
              </div>

              <CTAContent />
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
